import React from "react";
import { useHistory } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import InventoryIcon from "@mui/icons-material/Inventory";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import DoneIcon from "@mui/icons-material/Done";

import useDashboard from "../hooks/useDashboard";
import StatCard from "./Dashboard/StatCard";
import DashboardFilters from "./Dashboard/DashboardFilters";
import DashboardChart from "./Dashboard/DashboardChart";
import DashboardPDF from "./PDF/DashboardPDF";
import { formatCurrency } from "../utils/formatters";

const actualBars = [
  { dataKey: "total_base_price", name: "Total Harga Dasar", color: "#8884d8" },
  { dataKey: "total_sales", name: "Total Penjualan", color: "#82ca9d" },
  { dataKey: "total_profit", name: "Total Laba", color: "#84d8c7" },
  { dataKey: "total_discount", name: "Total Diskon", color: "#d8d884" },
  {
    dataKey: "total_platform_fee_bill",
    name: "Total Platform Fee Tagihan",
    color: "#CBA0AE",
  },
  {
    dataKey: "total_platform_fee_invoice",
    name: "Total Platform Fee Nota",
    color: "#0096FF",
  },
  { dataKey: "total_delivery_fee", name: "Total Ongkir", color: "#37B828" },
];

const estimateBars = [
  {
    dataKey: "estimate_total_sales",
    name: "Estimasi Total Penjualan",
    color: "#8884d8",
  },
  { dataKey: "estimate_profit", name: "Estimasi Total Laba", color: "#82ca9d" },
];

const Dashboard = () => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    year,
    setYear,
    employee,
    setEmployee,
    years,
    employees,
    dashboard,
    summary,
    dashboardEstimate,
    isLoading,
  } = useDashboard();

  const handleExport = () => {
    DashboardPDF(dashboard, dashboardEstimate, {
      year,
      employees,
      employee,
    });
  };

  const handleNewOrderClick = () => {
    history.push(`/admin/data_order?status=new&year=${year}`);
  };

  const handleProcessOrderClick = () => {
    history.push(`/admin/data_order?status=process&year=${year}`);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          mb: { xs: 2, sm: 3 },
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        <Box>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            fontWeight="bold"
            color="text.primary"
          >
            Dashboard
          </Typography>
          {!isMobile && (
            <Typography variant="body2" color="text.secondary">
              Ringkasan pesanan dan performa penjualan
            </Typography>
          )}
        </Box>
        <DashboardFilters
          year={year}
          employee={employee}
          employees={employees}
          years={years}
          onYearChange={setYear}
          onEmployeeChange={setEmployee}
          onExport={handleExport}
          loading={isLoading}
        />
      </Box>

      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? (
            <Skeleton
              variant="rectangular"
              height={isMobile ? 90 : 120}
              sx={{ borderRadius: 2 }}
            />
          ) : (
            <StatCard
              icon={InventoryIcon}
              title="Total Pesanan"
              value={summary?.total_order || 0}
              color="primary"
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? (
            <Skeleton
              variant="rectangular"
              height={isMobile ? 90 : 120}
              sx={{ borderRadius: 2 }}
            />
          ) : (
            <StatCard
              icon={ShoppingCartIcon}
              title="Pesanan Baru"
              value={summary?.new_order || 0}
              color="info"
              onClick={handleNewOrderClick}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? (
            <Skeleton
              variant="rectangular"
              height={isMobile ? 90 : 120}
              sx={{ borderRadius: 2 }}
            />
          ) : (
            <StatCard
              icon={AutorenewIcon}
              title="Pesanan Diproses"
              value={summary?.process_order || 0}
              color="warning"
              onClick={handleProcessOrderClick}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {isLoading ? (
            <Skeleton
              variant="rectangular"
              height={isMobile ? 90 : 120}
              sx={{ borderRadius: 2 }}
            />
          ) : (
            <StatCard
              icon={DoneIcon}
              title="Total Selesai"
              value={summary?.done_order || 0}
              color="success"
            />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12}>
          <DashboardChart
            title="Performa Penjualan per Cawu"
            data={dashboard}
            bars={actualBars}
            xTickFormatter={(value) => `Cawu ${value}`}
            tooltipLabelFormatter={(value) => `Cawu ${value} Tahun ${year}`}
            tooltipValueFormatter={(value, name) => [
              formatCurrency(value),
              name,
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          <DashboardChart
            title="Estimasi Penjualan per Cawu"
            data={dashboardEstimate}
            bars={estimateBars}
            xTickFormatter={(value) => `Cawu ${value}`}
            tooltipLabelFormatter={(value) => `Cawu ${value} Tahun ${year}`}
            tooltipValueFormatter={(value, name) => [
              formatCurrency(value),
              name,
            ]}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
